import { isInvoiceFinalized } from "@/lib/invoiceStatus";
import type { Invoice } from "@/types/camping";

const STAY_INVOICE_TYPES = new Set(["deposit", "full", "final"]);

export interface StayPaymentSource {
  total_amount: string;
  currency: string;
  price_pending?: boolean;
  deposit_paid_amount?: string | null;
}

/** Aufenthalt gegen bereits ausgestellte Rechnungen, ohne Strom-Nachzahlungen. */
export interface StayPaymentGap {
  stayTotal: number;
  /** Anzahlung, Voll- und Schlussrechnung, abzüglich Gutschriften darauf. */
  invoicedAmount: number;
  paidAmount: number;
  /** Teil des Aufenthalts, für den noch keine Rechnung ausgestellt wurde. */
  notInvoicedAmount: number;
  currency: string;
  /** Eine Voll- oder Schlussrechnung ist ausgestellt. */
  stayInvoiced: boolean;
}

function cents(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100) / 100;
}

function amount(value: string | number | null | undefined): number {
  const num = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isFinite(num) ? num : 0;
}

/**
 * Rest des Aufenthalts, der noch nicht auf einer Rechnung steht.
 * Eine bezahlte Anzahlung allein deckt den Aufenthalt nicht ab.
 */
export function stayPaymentGap(
  booking: StayPaymentSource,
  invoices: Invoice[],
): StayPaymentGap | null {
  if (booking.price_pending) return null;
  const stayTotal = amount(booking.total_amount);
  if (!Number.isFinite(stayTotal)) return null;

  const active = invoices.filter((invoice) => invoice.status !== "cancelled");
  const issuedStay = active.filter(
    (invoice) =>
      STAY_INVOICE_TYPES.has(invoice.invoice_type) && isInvoiceFinalized(invoice.status),
  );
  const stayIds = new Set(issuedStay.map((invoice) => invoice.id));
  const credited = active
    .filter(
      (invoice) =>
        invoice.invoice_type === "credit_note" &&
        isInvoiceFinalized(invoice.status) &&
        invoice.reference_invoice_id != null &&
        stayIds.has(invoice.reference_invoice_id),
    )
    .reduce((sum, invoice) => sum + amount(invoice.total_amount), 0);

  const invoicedAmount = cents(
    Math.max(0, issuedStay.reduce((sum, invoice) => sum + amount(invoice.total_amount), 0) - credited),
  );
  const stayInvoiced =
    issuedStay.some((invoice) => invoice.invoice_type === "full" || invoice.invoice_type === "final") ||
    invoicedAmount + 0.009 >= cents(stayTotal);
  const notInvoicedAmount = stayInvoiced ? 0 : cents(Math.max(0, stayTotal - invoicedAmount));

  const paidOnInvoices = issuedStay.reduce((sum, invoice) => sum + amount(invoice.paid_amount), 0);
  const hasIssuedDeposit = issuedStay.some((invoice) => invoice.invoice_type === "deposit");
  const prepaid = hasIssuedDeposit ? 0 : amount(booking.deposit_paid_amount);
  const paidAmount = cents(Math.max(0, paidOnInvoices + prepaid));

  return {
    stayTotal: cents(stayTotal),
    invoicedAmount,
    paidAmount,
    notInvoicedAmount,
    currency: booking.currency || "EUR",
    stayInvoiced,
  };
}

export function hasNotInvoicedStayAmount(gap: StayPaymentGap | null | undefined): boolean {
  return gap != null && gap.notInvoicedAmount > 0.009;
}
